import HeaderContent from "@/components/controls/DataTable/TableHeader/HeaderContent";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import { Filters, FiltersSetter } from "@/services/api/types";
import { cn } from "@/utils/utils";
import { Draggable } from "@hello-pangea/dnd";
import { Header } from "@tanstack/react-table";
import { ReactNode } from "react";

export const TableHeader: React.FC<{
  header: Header<any, unknown>;
  index: number;
  enableColumnReorder: boolean;
  stickyLeftColumn?: boolean;
  isPositionLocked?: boolean;
  resizableColumns?: boolean;
  currentGroupBy: string[];
  renderResize: (
    header: Header<any, unknown>,
    isHeaderRow?: boolean,
    isStickyLeftColumn?: boolean
  ) => ReactNode;
  hoveredColumnId: string | null;
  handleColumnHover: (columnId: string | null) => void;
  className?: string;
  filterOptions?: DataTableFilter[];
  filters?: Filters;
  setFilters?: FiltersSetter;
}> = ({
  header,
  index,
  enableColumnReorder,
  stickyLeftColumn,
  isPositionLocked = false,
  resizableColumns,
  renderResize,
  hoveredColumnId,
  handleColumnHover,
  className,
  filterOptions,
  filters,
  setFilters
}) => {
  const isSortable = header.column.getCanSort();
  const sortDirection = header.column.getIsSorted();
  const isHovered = hoveredColumnId === header.id;
  const isDraggingDisabled =
    isPositionLocked ||
    (header.column.columnDef.meta as any)?.disableDragging === true;
  const hideSeparator =
    (header.column.columnDef.meta as any)?.hideSeparator === true;
  const isStickyLeftColumn = !!stickyLeftColumn && index === 0;

  const getInnerClassName = (paddingClass?: string) =>
    cn(
      "h-full relative pl-4",
      stickyLeftColumn && index === 0 && !hideSeparator
        ? "after:content-[''] after:absolute after:top-0 after:right-[-1px] after:bottom-[-1px] after:w-px after:bg-border-border1"
        : "",
      paddingClass
    );

  const getThClassName = (isDragging: boolean = false) =>
    cn(
      "h-12 text-sm font-semibold align-bottom text-secondary select-none",
      `column-width-${header.getSize()}`,
      resizableColumns ? "relative" : "",
      stickyLeftColumn && index === 0
        ? "sticky left-0 z-30 bg-surface-elevation0"
        : "",
      enableColumnReorder
        ? cn(
            "bg-surface-elevation0",
            isDragging ? "bg-surface-elevation2" : "",
            "after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-[-2px] after:h-[0.5px] after:bg-border-border1 after:z-10"
          )
        : "border-t border-border-border1",
      className
    );

  const thStyle = {
    width: header.getSize(),
    minWidth: header.getSize(),
    maxWidth: header.getSize()
  };

  if (!enableColumnReorder) {
    return (
      <th key={header.id} className={getThClassName()} style={thStyle}>
        <div
          className={getInnerClassName()}
          onMouseEnter={() => handleColumnHover(header.id)}
          onMouseLeave={() => handleColumnHover(null)}
        >
          <HeaderContent
            header={header}
            isSortable={isSortable}
            sortDirection={sortDirection}
            isHovered={isHovered}
            filterOptions={filterOptions}
            filters={filters}
            setFilters={setFilters}
          />
          {resizableColumns && renderResize(header, true, isStickyLeftColumn)}
        </div>
      </th>
    );
  }

  // Every header stays a Draggable, disabled ones included, so the library sees consecutive indices.
  return (
    <Draggable
      draggableId={header.id}
      index={index}
      isDragDisabled={isDraggingDisabled}
    >
      {(provided, snapshot) => (
        <th
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={getThClassName(snapshot.isDragging)}
          style={{
            ...thStyle,
            ...provided.draggableProps.style
          }}
        >
          <div
            className={getInnerClassName(isDraggingDisabled ? undefined : "pl-1")}
            onMouseEnter={() => handleColumnHover(header.id)}
            onMouseLeave={() => handleColumnHover(null)}
          >
            <HeaderContent
              header={header}
              isSortable={isSortable}
              sortDirection={sortDirection}
              isHovered={isHovered}
              showDragHandle={!isDraggingDisabled}
              dragHandleProps={provided.dragHandleProps}
              filterOptions={filterOptions}
              filters={filters}
              setFilters={setFilters}
            />
            {resizableColumns && renderResize(header, true, isStickyLeftColumn)}
          </div>
        </th>
      )}
    </Draggable>
  );
};
